import { useEffect, useState } from "react";
import { Alert, Box, IconButton, Tooltip } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import { GridLoader } from "react-spinners";
import { useNavigate } from "react-router-dom";
import Loading from "../../../components/Loading";
import Pagination from "../../../components/Pagination";
import ApuracaoCardShell from "../../Apuracoes/components/ApuracaoCardShell";
import { AcaoIcone } from "../../../components/PadraoTela";
import BadgeStatus from "../../Apuracoes/components/BadgeStatus";
import SelectFiltro, { BuscaFiltro } from "../../Apuracoes/components/SelectFiltro";
import { formatarCnpj } from "../../Apuracoes/utils";
import { formatarMoeda } from "../../Apuracoes/Detalhe/utils/agregarPorConta.util";
import { listarProjetos } from "../../../services/projetos-apuracao.service";
import { STATUS_LABEL, TIPO_LABEL, formatarData } from "../utils/projeto.util";
import { montarUrlApuracao } from "../utils/url.util";
import styles from "./styles.module.css";

const ATRASO_FILTRO_MS = 300;

const FILTROS_VAZIOS = { idProjeto: "", razaoSocial: "", cnpj: "", tipo: "", status: "" };

const OPCOES_TIPO = [
  { value: "", label: "Todos" },
  ...Object.entries(TIPO_LABEL).map(([value, label]) => ({ value, label })),
];

const OPCOES_POR_PAGINA = [10, 50, 100];

const OPCOES_STATUS = [
  { value: "", label: "Todos" },
  ...Object.entries(STATUS_LABEL).map(([value, label]) => ({ value, label })),
];

const TOM_POR_STATUS = { APROVADO: "ok", ENVIADO: "alerta" };

function parametrosDaConsulta(page, limit, filtros) {
  const params = { page, limit };
  Object.entries(filtros).forEach(([chave, valor]) => {
    const texto = String(valor ?? "").trim();
    if (texto) params[chave] = texto;
  });
  return params;
}

/**
 * TXM-558 — Conta Corrente → Projetos: lista de projetos de apuração com filtros e paginação no
 * servidor. As ações levam ao Resumo do Projeto e à apuração de origem.
 */
export default function ListaProjetosApuracao() {
  const navigate = useNavigate();
  const [filtros, setFiltros] = useState(FILTROS_VAZIOS);
  const [aplicados, setAplicados] = useState(FILTROS_VAZIOS);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [projetos, setProjetos] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [atualizacao, setAtualizacao] = useState(0);

  // Digitar não dispara uma consulta por tecla: os filtros valem depois de uma pausa curta.
  useEffect(() => {
    if (filtros === aplicados) return undefined;
    const espera = setTimeout(() => {
      setAplicados(filtros);
      setPage(1);
    }, ATRASO_FILTRO_MS);
    return () => clearTimeout(espera);
  }, [filtros, aplicados]);

  useEffect(() => {
    let ativo = true;
    (async () => {
      setLoading(true);
      const res = await listarProjetos(parametrosDaConsulta(page, limit, aplicados));
      if (!ativo) return;
      if (res?.sucesso) {
        setErro("");
        setProjetos(Array.isArray(res.projetos) ? res.projetos : []);
        setTotal(typeof res.total === "number" ? res.total : 0);
      } else {
        setErro(res?.mensagem || "Não foi possível carregar os projetos.");
        setProjetos([]);
        setTotal(0);
      }
      setLoading(false);
    })();
    return () => {
      ativo = false;
    };
  }, [page, limit, aplicados, atualizacao]);

  const definirFiltro = (campo) => (valor) => setFiltros((atual) => ({ ...atual, [campo]: valor }));
  const carregandoInicial = loading && projetos.length === 0 && !erro;

  const filtrosDaTela = (
    <>
      <BuscaFiltro
        label="ID do Projeto"
        placeholder="Ex.: PRJ-0007"
        value={filtros.idProjeto}
        onChange={definirFiltro("idProjeto")}
      />
      <BuscaFiltro
        label="Razão Social"
        placeholder="Filtrar por nome..."
        value={filtros.razaoSocial}
        onChange={definirFiltro("razaoSocial")}
      />
      <BuscaFiltro
        label="CNPJ"
        placeholder="Somente números ou formatado"
        value={filtros.cnpj}
        onChange={definirFiltro("cnpj")}
      />
      <SelectFiltro label="Tipo" value={filtros.tipo} onChange={definirFiltro("tipo")} options={OPCOES_TIPO} />
      <SelectFiltro
        label="Status"
        value={filtros.status}
        onChange={definirFiltro("status")}
        options={OPCOES_STATUS}
      />
      <Tooltip title="Atualizar">
        <IconButton
          size="small"
          onClick={() => setAtualizacao((n) => n + 1)}
          sx={{ color: "#1b3a5f" }}
          aria-label="Atualizar lista"
        >
          <RefreshIcon />
        </IconButton>
      </Tooltip>
    </>
  );

  return (
    <>
      {erro && (
        <Alert severity="error" sx={{ mx: "2rem", mb: 2 }}>
          {erro}
        </Alert>
      )}

      <ApuracaoCardShell
        Icone={AssignmentOutlinedIcon}
        titulo="Projetos"
        subtitulo="Projetos de apuração por cliente, do envio à aprovação"
        filtros={filtrosDaTela}
        paginacao={
          !carregandoInicial && (
            <Pagination
              paginationKey="projetos-apuracao-lista"
              opcoesPorPagina={OPCOES_POR_PAGINA}
              serverSideTotal={total}
              serverSidePage={page}
              serverSideRowsPerPage={limit}
              onServerPageChange={setPage}
              onServerRowsPerPageChange={(novoLimite) => {
                setLimit(novoLimite);
                setPage(1);
              }}
              disabled={loading}
            />
          )
        }
      >
        {carregandoInicial ? (
          <div className={styles.loadingWrap}>
            <Loading loading />
          </div>
        ) : (
          <div className={styles.tableSection}>
            {loading && (
              <div className={styles.tableOverlay} aria-hidden="true">
                <GridLoader color="#1b3a5f" loading size={8} />
              </div>
            )}
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.colId}>ID do Projeto</th>
                  <th className={styles.colCnpj}>CNPJ</th>
                  <th className={styles.colRazao}>Razão Social</th>
                  <th className={styles.colTipo}>Tipo</th>
                  <th className={styles.colData}>Data Criação</th>
                  <th className={styles.colValor}>Valor Total</th>
                  <th className={styles.colStatus}>Status</th>
                  <th className={styles.colAcoes}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {projetos.length === 0 ? (
                  <tr className={styles.emptyRow}>
                    <td colSpan={8}>{erro ? "—" : "Nenhum projeto encontrado."}</td>
                  </tr>
                ) : (
                  projetos.map((projeto) => (
                    <tr key={projeto.idProjeto}>
                      <td className={styles.colId}>{projeto.codigo}</td>
                      <td className={styles.colCnpj}>{formatarCnpj(projeto.cnpj)}</td>
                      <td className={styles.colRazao}>
                        <Tooltip title={projeto.razaoSocial} arrow placement="top">
                          <span>{projeto.razaoSocial}</span>
                        </Tooltip>
                      </td>
                      <td className={styles.colTipo}>{TIPO_LABEL[projeto.tipo] || projeto.tipo}</td>
                      <td className={styles.colData}>{formatarData(projeto.dtCriacao)}</td>
                      <td className={styles.colValor}>{formatarMoeda(projeto.valorTotal)}</td>
                      <td className={styles.colStatus}>
                        <BadgeStatus tom={TOM_POR_STATUS[projeto.status] || "neutro"}>
                          {STATUS_LABEL[projeto.status] || projeto.status}
                        </BadgeStatus>
                      </td>
                      <td className={styles.colAcoes}>
                        <Box className={styles.acoesCell}>
                          <AcaoIcone
                            titulo="Ver Resumo"
                            onClick={() => navigate(`/conta-corrente/projetos/${projeto.idProjeto}`)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </AcaoIcone>
                          <AcaoIcone titulo="Ver Apuração" onClick={() => navigate(montarUrlApuracao(projeto))}>
                            <TravelExploreIcon fontSize="small" />
                          </AcaoIcone>
                        </Box>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </ApuracaoCardShell>
    </>
  );
}
